/**
 * Revenue tab — tier ladder, MRR/ARR, transitions, annual mix.
 *
 * Includes an "Include beta family" toggle (default OFF). Beta-family users
 * are flagged scope='revenue_only' in analytics_account_exclusions — their
 * paid subscriptions are comp'd, so excluding them gives the real-money
 * picture. Flip the toggle ON to see what numbers look like if they were
 * treated as real subscribers. Affects every revenue query on this tab.
 *
 * Engagement-side tabs (Engagement / Retention / Relationship) ignore this
 * toggle — beta family always counts there because their engagement is real.
 */
import React, { useState } from 'react';
import { Users } from 'lucide-react';
import { useAnalyticsQuery } from '../hooks/useAnalyticsQuery';
import { type AnalyticsRange } from '../hooks/useAnalyticsRange';
import type {
  AnnualMixPayload,
  MrrArrPayload,
  TierLadderPayload,
  TransitionsPayload,
} from '../types';
import { ChartCard } from '../components/ChartCard';
import { KpiTile } from '../components/KpiTile';
import { TierLadderChart } from '../components/TierLadderChart';
import { TrendChart } from '../components/TrendChart';
import { formatCurrency, formatInt, formatPercent } from '../format';
import { TIER_DISPLAY } from '../theme';
import { cn } from '../../../../lib/cn';

export interface RevenueTabProps {
  range: AnalyticsRange;
}

export const RevenueTab: React.FC<RevenueTabProps> = ({ range }) => {
  // Default OFF — the standard MRR view excludes beta family. Toggle locally;
  // doesn't persist (intentional — it's a "what if" lens, not a setting).
  const [includeBeta, setIncludeBeta] = useState(false);

  // Query strings: append include_beta only when true so the cache key for
  // the default view is the cleanest (no trailing param). The backend treats
  // a missing param as false.
  const betaParam = includeBeta ? '&include_beta=true' : '';
  const cacheKey = includeBeta ? 'beta-on' : 'beta-off';

  const ladder = useAnalyticsQuery<TierLadderPayload>(
    ['tier-ladder', range.queryString, cacheKey],
    `/api/admin/analytics/revenue/tier-ladder?${range.queryString}${betaParam}`,
  );
  const mrrArr = useAnalyticsQuery<MrrArrPayload>(
    ['mrr-arr', range.queryString, cacheKey],
    `/api/admin/analytics/revenue/mrr-arr?${range.queryString}${betaParam}`,
  );
  const transitions = useAnalyticsQuery<TransitionsPayload>(
    ['transitions', range.queryString, cacheKey],
    `/api/admin/analytics/revenue/transitions?${range.queryString}${betaParam}`,
  );
  // Range param was previously omitted, freezing this panel at the backend's
  // 30-day default no matter what the picker said (audit F13).
  const annualMix = useAnalyticsQuery<AnnualMixPayload>(
    ['annual-mix', range.queryString, cacheKey],
    `/api/admin/analytics/revenue/annual-mix?${range.queryString}${betaParam}`,
  );

  const current = mrrArr.data?.data.current;
  const summary = transitions.data?.data.summary ?? {};

  return (
    <div className="space-y-6">
      {/* Include-beta toggle. Default off = real revenue picture; on = adds the
          revenue_only cohort (beta family) back in for "what if" comparison. */}
      <div className="flex items-center justify-end gap-2 text-xs text-gray-500">
        <Users className="h-4 w-4 text-emerald-600" aria-hidden />
        <span>Include beta family in revenue?</span>
        <button
          type="button"
          role="switch"
          aria-checked={includeBeta}
          onClick={() => setIncludeBeta((v) => !v)}
          className={cn(
            'relative inline-flex h-5 w-9 items-center rounded-full transition-colors',
            includeBeta ? 'bg-emerald-500' : 'bg-gray-300',
          )}
        >
          <span
            className={cn(
              'inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform',
              includeBeta ? 'translate-x-4' : 'translate-x-0.5',
            )}
          />
        </button>
        <span className={includeBeta ? 'font-semibold text-emerald-700' : ''}>
          {includeBeta ? 'ON' : 'OFF'}
        </span>
      </div>

      {/* Top KPIs */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <KpiTile
          label="Current MRR"
          value={formatCurrency(current?.mrr ?? 0)}
          hint={
            current
              ? `${formatInt(current.paying_users)} paying · live`
                + (current.by_tier
                    ? ` (${Object.entries(current.by_tier)
                        .map(([t, n]) => `${n} ${t}`).join(', ')})`
                    : '')
              : undefined
          }
          loading={mrrArr.isLoading}
        />
        <KpiTile
          label="Current ARR"
          value={formatCurrency(current?.arr ?? 0, 0)}
          loading={mrrArr.isLoading}
        />
        <KpiTile
          label="Upgrades (range)"
          value={formatInt(summary.upgrades ?? 0)}
          hint={`${formatInt(summary.downgrades ?? 0)} downgrades`}
          loading={transitions.isLoading}
        />
        <KpiTile
          label="Cancellations (range)"
          value={formatInt(summary.cancels ?? 0)}
          negativeIsGood
          loading={transitions.isLoading}
        />
      </div>

      {/* Tier ladder */}
      <ChartCard
        title="Tier ladder"
        subtitle={
          ladder.data?.data.scope === 'subscribed'
            ? 'Subscribers by tier (revenue scope — matches RevenueCat). Days before the snapshot went live show active-users as a proxy.'
            : 'Active users stacked by tier, over time (subscriber snapshot not yet collecting)'
        }
        loading={ladder.isLoading}
        error={ladder.error?.message ?? null}
        isEmpty={!ladder.data?.data.series?.length}
        onRetry={() => ladder.refetch()}
      >
        <TierLadderChart series={ladder.data?.data.series ?? []} />
      </ChartCard>

      {/* MRR trend */}
      <ChartCard
        title="MRR / ARR"
        subtitle={
          mrrArr.data?.data.series_basis === 'subscribed_daily'
            ? 'Real subscribers × period-aware pricing (matches the Current MRR tile). Pre-snapshot days are an active-users proxy.'
            : 'Projected from active_by_tier × tier pricing (subscriber snapshot not yet collecting)'
        }
        loading={mrrArr.isLoading}
        error={mrrArr.error?.message ?? null}
        isEmpty={!mrrArr.data?.data.series?.length}
        onRetry={() => mrrArr.refetch()}
      >
        <TrendChart
          data={mrrArr.data?.data.series ?? []}
          xKey="day"
          type="area"
          height={260}
          series={[{ key: 'mrr', label: 'MRR' }]}
          yFormat={(v) => formatCurrency(v, 0)}
          xFormat={formatDayShort}
        />
      </ChartCard>

      {/* Transitions + Annual mix */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <ChartCard
          title="Tier transitions"
          subtitle="Tier-to-tier flows in the selected range"
          loading={transitions.isLoading}
          error={transitions.error?.message ?? null}
          isEmpty={!transitions.data?.data.edges?.length}
          onRetry={() => transitions.refetch()}
          className="lg:col-span-2"
        >
          <TransitionsTable edges={transitions.data?.data.edges ?? []} />
        </ChartCard>

        <ChartCard
          title="Annual mix"
          subtitle="Share of Chat+ and Pro subscribers on annual plans"
          loading={annualMix.isLoading}
          error={annualMix.error?.message ?? null}
          isEmpty={false}
          onRetry={() => annualMix.refetch()}
          className="lg:col-span-1"
        >
          <AnnualMixPanel data={annualMix.data?.data} />
        </ChartCard>
      </div>
    </div>
  );
};

const TransitionsTable: React.FC<{
  edges: TransitionsPayload['edges'];
}> = ({ edges }) => {
  if (!edges.length) return null;
  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200 text-sm">
        <thead className="bg-gray-50/60">
          <tr>
            <th className="px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wide text-gray-500">
              From
            </th>
            <th className="px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wide text-gray-500">
              To
            </th>
            <th className="px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wide text-gray-500">
              Type
            </th>
            <th className="px-3 py-2 text-right text-[11px] font-semibold uppercase tracking-wide text-gray-500">
              Count
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100 bg-white">
          {edges.map((e, i) => (
            <tr key={`${e.from}-${e.to}-${e.transition_type}-${i}`} className="hover:bg-gray-50">
              <td className="px-3 py-1.5 text-gray-700">{TIER_DISPLAY[e.from] ?? e.from}</td>
              <td className="px-3 py-1.5 text-gray-700">{TIER_DISPLAY[e.to] ?? e.to}</td>
              <td className="px-3 py-1.5 text-[11px] font-mono text-gray-500">
                {e.transition_type}
              </td>
              <td className="px-3 py-1.5 text-right tabular-nums text-gray-800">
                {formatInt(e.count)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const AnnualMixPanel: React.FC<{ data?: AnnualMixPayload }> = ({ data }) => {
  if (!data) return null;
  return (
    <div className="space-y-4">
      <AnnualMixRow label="Chat+" bucket={data.chat_plus} />
      <AnnualMixRow label="Pro" bucket={data.pro} />
    </div>
  );
};

const AnnualMixRow: React.FC<{
  label: string;
  bucket: { monthly: number; annual: number; annual_pct: number };
}> = ({ label, bucket }) => {
  const total = bucket.monthly + bucket.annual;
  return (
    <div>
      <div className="flex items-baseline justify-between text-sm">
        <span className="font-semibold text-gray-900">{label}</span>
        <span className="font-semibold tabular-nums text-gray-800">
          {formatPercent(bucket.annual_pct)} annual
        </span>
      </div>
      <div className="mt-1 flex h-3 overflow-hidden rounded-full bg-gray-100">
        {total > 0 && (
          <>
            <div
              className="bg-coach-cedar-stream"
              style={{ width: `${(bucket.annual / total) * 100}%` }}
            />
            <div
              className="bg-gray-200"
              style={{ width: `${(bucket.monthly / total) * 100}%` }}
            />
          </>
        )}
      </div>
      <div className="mt-1 flex justify-between text-[10.5px] text-gray-500">
        <span>{formatInt(bucket.annual)} annual</span>
        <span>{formatInt(bucket.monthly)} monthly</span>
      </div>
    </div>
  );
};

function formatDayShort(iso: string): string {
  const d = new Date(iso);
  if (isNaN(d.valueOf())) return iso;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}
